import React, { useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'trp_clarity_config';

const DIMENSION_OPTIONS = [
  '',
  'Browser',
  'Device',
  'Country/Region',
  'OS',
  'Source',
  'Medium',
  'Campaign',
  'Channel',
  'URL',
];

const numberOrZero = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (value) => {
  return new Intl.NumberFormat().format(numberOrZero(value));
};

const formatDuration = (seconds) => {
  const s = numberOrZero(seconds);
  if (s < 60) return `${Math.round(s)}초`;
  if (s < 3600) return `${Math.floor(s / 60)}분 ${Math.round(s % 60)}초`;
  return `${Math.floor(s / 3600)}시간 ${Math.floor((s % 3600) / 60)}분`;
};

const formatPercent = (value) => {
  return `${numberOrZero(value).toFixed(1)}%`;
};

// 간단한 바 차트 컴포넌트
const SimpleBarChart = ({ data, labelKey, valueKey, maxItems = 10, color = 'blue' }) => {
  if (!data || data.length === 0) return <div className="text-sm text-gray-500">데이터 없음</div>;
  
  const sortedData = [...data]
    .sort((a, b) => numberOrZero(b[valueKey]) - numberOrZero(a[valueKey]))
    .slice(0, maxItems);
  
  const maxValue = Math.max(...sortedData.map(d => numberOrZero(d[valueKey])), 1);
  
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
  };
  
  return (
    <div className="space-y-2">
      {sortedData.map((item, idx) => {
        const value = numberOrZero(item[valueKey]);
        const percent = (value / maxValue) * 100;
        const label = item[labelKey] || '(알 수 없음)';
        
        return (
          <div key={idx} className="group">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-700 truncate max-w-[200px]" title={label}>
                {label.length > 30 ? label.substring(0, 30) + '...' : label}
              </span>
              <span className="text-gray-600 font-medium ml-2">{formatNumber(value)}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${colorClasses[color] || colorClasses.blue} rounded-full transition-all duration-300`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 도넛 차트 컴포넌트 (범례만)
const SimpleDonutChart = ({ data, labelKey, valueKey, maxItems = 6 }) => {
  if (!data || data.length === 0) return <div className="text-sm text-gray-500">데이터 없음</div>;
  
  const sortedData = [...data]
    .sort((a, b) => numberOrZero(b[valueKey]) - numberOrZero(a[valueKey]))
    .slice(0, maxItems);
  
  const total = sortedData.reduce((sum, d) => sum + numberOrZero(d[valueKey]), 0);
  
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
    'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'
  ];
  const textColors = [
    'text-blue-600', 'text-green-600', 'text-purple-600',
    'text-amber-600', 'text-rose-600', 'text-cyan-600'
  ];
  
  return (
    <div className="space-y-1.5">
      {sortedData.map((item, idx) => {
        const value = numberOrZero(item[valueKey]);
        const percent = total > 0 ? (value / total) * 100 : 0;
        const label = item[labelKey] || '(알 수 없음)';
        
        return (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div className={`w-3 h-3 rounded-sm flex-shrink-0 ${colors[idx % colors.length]}`} />
            <span className="text-gray-700 truncate flex-1" title={label}>
              {label.length > 20 ? label.substring(0, 20) + '...' : label}
            </span>
            <span className={`font-semibold ${textColors[idx % textColors.length]}`}>
              {percent.toFixed(1)}%
            </span>
            <span className="text-gray-500 text-[10px]">({formatNumber(value)})</span>
          </div>
        );
      })}
    </div>
  );
};

const ClarityAnalyticsPage = () => {
  const [config, setConfig] = useState({
    siteUrl: '',
    token: '',
    numOfDays: '1',
    dimension1: '',
    dimension2: '',
    dimension3: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null); // 한 번의 호출로 얻은 데이터
  const [lastFetched, setLastFetched] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [viewMode, setViewMode] = useState('simple'); // 'simple' | 'advanced'
  const [advancedData, setAdvancedData] = useState([]);

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setConfig((prev) => ({ ...prev, ...parsed }));
    } catch (_) {
      // ignore parse errors
    }
  }, []);

  const saveConfig = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      window.alert('설정이 저장되었습니다.');
    } catch (_) {
      window.alert('설정을 저장하지 못했습니다.');
    }
  };

  const clearToken = () => {
    setConfig((prev) => ({ ...prev, token: '' }));
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        parsed.token = '';
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      }
    } catch (_) {}
  };

  // 단일 API 호출 함수
  const fetchClarityData = useCallback(async (dimensions = {}) => {
    if (!config.token) {
      setError('Clarity 토큰을 입력해 주세요. 상단의 ⚙️ 설정을 클릭하세요.');
      setShowSettings(true);
      return null;
    }
    
    const params = new URLSearchParams();
    params.set('numOfDays', config.numOfDays || '1');
    if (dimensions.dimension1) params.set('dimension1', dimensions.dimension1);
    if (dimensions.dimension2) params.set('dimension2', dimensions.dimension2);
    if (dimensions.dimension3) params.set('dimension3', dimensions.dimension3);

    const res = await fetch(`https://www.clarity.ms/export-data/api/v1/project-live-insights?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.token}`,
      },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status} 오류: ${text || 'API 호출에 실패했습니다.'}`);
    }

    const json = await res.json();
    if (!Array.isArray(json)) {
      throw new Error('예상치 못한 응답 형식입니다.');
    }
    
    return json;
  }, [config.token, config.numOfDays]);

  // 대시보드 데이터 로드 (한 번의 최적화된 호출)
  // Device + Source 조합으로 기기별, 유입경로별 데이터를 동시에 얻음
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      // 최적의 Dimension 조합: Device + Source
      // 이 조합으로 기기 분포 + 유입 경로를 한 번에 얻을 수 있음
      const data = await fetchClarityData({
        dimension1: 'Device',
        dimension2: 'Source',
        dimension3: '',
      });
      
      if (data) {
        console.log('[Clarity Dashboard] API Response:', data);
        data.forEach(metric => {
          console.log(`  ${metric.metricName}:`, metric.information?.slice(0, 2));
        });
        
        setDashboardData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'API 호출 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [fetchClarityData]);

  // 고급 모드 데이터 로드
  const loadAdvancedData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await fetchClarityData({
        dimension1: config.dimension1,
        dimension2: config.dimension2,
        dimension3: config.dimension3,
      });
      
      if (data) {
        setAdvancedData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'API 호출 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [fetchClarityData, config.dimension1, config.dimension2, config.dimension3]);

  // 메트릭 찾기 헬퍼
  const getMetric = useCallback((metricName) => {
    if (!dashboardData) return null;
    return dashboardData.find(m => m.metricName === metricName);
  }, [dashboardData]);

  // 트래픽 요약 계산
  const trafficSummary = useMemo(() => {
    const traffic = getMetric('Traffic');
    if (!traffic || !Array.isArray(traffic.information)) return null;
    
    const info = traffic.information;
    
    // 전체 집계
    let totalSessions = 0;
    let totalBot = 0;
    let totalDistinctUsers = 0;
    let totalPagesPerSession = 0;
    let sessionCountForPPS = 0;
    
    // Device별, Source별 집계
    const deviceMap = {};
    const sourceMap = {};
    
    info.forEach(row => {
      const sessions = numberOrZero(row.totalSessionCount);
      const botSessions = numberOrZero(row.totalBotSessionCount);
      const distinctUsers = numberOrZero(row.distinctUserCount);
      const pps = numberOrZero(row.pagesPerSessionPercentage); // 실제 필드명
      
      totalSessions += sessions;
      totalBot += botSessions;
      totalDistinctUsers += distinctUsers;
      
      if (sessions > 0 && pps > 0) {
        totalPagesPerSession += sessions * pps;
        sessionCountForPPS += sessions;
      }
      
      // Device 집계 (봇 제외)
      const device = row.Device || '기타';
      if (!deviceMap[device]) deviceMap[device] = { name: device, sessions: 0, users: 0 };
      deviceMap[device].sessions += sessions;
      deviceMap[device].users += distinctUsers;
      
      // Source 집계
      const source = row.Source || '(직접 방문)';
      if (!sourceMap[source]) sourceMap[source] = { name: source, sessions: 0, users: 0 };
      sourceMap[source].sessions += sessions;
      sourceMap[source].users += distinctUsers;
    });
    
    return {
      totalSessions,
      totalBot,
      humanSessions: Math.max(totalSessions - totalBot, 0),
      distinctUsers: totalDistinctUsers,
      pagesPerSession: sessionCountForPPS > 0 ? totalPagesPerSession / sessionCountForPPS : 0,
      devices: Object.values(deviceMap).sort((a, b) => b.sessions - a.sessions),
      sources: Object.values(sourceMap).sort((a, b) => b.sessions - a.sessions),
    };
  }, [getMetric]);

  // 체류시간 요약
  const engagementSummary = useMemo(() => {
    const engagement = getMetric('EngagementTime'); // 실제 메트릭명
    if (!engagement || !Array.isArray(engagement.information)) return null;
    
    const info = engagement.information;
    let totalActiveTime = 0;
    let totalTotalTime = 0;
    let rowCount = info.length;
    
    // 실제 필드: totalTime, activeTime (초 단위 합계)
    info.forEach(row => {
      totalActiveTime += numberOrZero(row.activeTime);
      totalTotalTime += numberOrZero(row.totalTime);
    });
    
    return {
      avgActiveTime: rowCount > 0 ? totalActiveTime / rowCount : 0,
      avgTotalTime: rowCount > 0 ? totalTotalTime / rowCount : 0,
      totalActiveTime,
      totalTotalTime,
    };
  }, [getMetric]);

  // 스크롤 깊이 요약
  const scrollSummary = useMemo(() => {
    const scroll = getMetric('ScrollDepth'); // 실제 메트릭명
    if (!scroll || !Array.isArray(scroll.information)) return null;
    
    const info = scroll.information;
    let totalScrollDepth = 0;
    let rowCount = info.length;
    
    // 실제 필드: averageScrollDepth
    info.forEach(row => {
      totalScrollDepth += numberOrZero(row.averageScrollDepth);
    });
    
    return {
      avgScrollDepth: rowCount > 0 ? totalScrollDepth / rowCount : 0,
    };
  }, [getMetric]);

  // UX 문제 지표
  const uxIssues = useMemo(() => {
    if (!dashboardData) return null;
    
    // 실제 메트릭명: DeadClickCount, RageClickCount, QuickbackClick
    // 실제 필드: subTotal, sessionsWithMetricPercentage
    const sumMetric = (metricName) => {
      const metric = getMetric(metricName);
      if (!metric || !Array.isArray(metric.information)) return { count: 0, percentage: 0 };
      
      let totalSubTotal = 0;
      let totalPercentage = 0;
      let rowCount = metric.information.length;
      
      metric.information.forEach(row => {
        totalSubTotal += numberOrZero(row.subTotal);
        totalPercentage += numberOrZero(row.sessionsWithMetricPercentage);
      });
      
      return {
        count: totalSubTotal,
        percentage: rowCount > 0 ? totalPercentage / rowCount : 0,
      };
    };
    
    return {
      deadClicks: sumMetric('DeadClickCount'),
      rageClicks: sumMetric('RageClickCount'),
      quickbacks: sumMetric('QuickbackClick'),
      excessiveScroll: sumMetric('ExcessiveScroll'),
      scriptErrors: sumMetric('ScriptErrorCount'),
      errorClicks: sumMetric('ErrorClickCount'),
    };
  }, [dashboardData, getMetric]);

  // 테이블 렌더링 (고급 모드용)
  const renderTable = (metric) => {
    if (!metric || !Array.isArray(metric.information) || metric.information.length === 0) {
      return <div className="text-sm text-gray-500">데이터가 없습니다.</div>;
    }
    const columns = Array.from(
      metric.information.reduce((set, row) => {
        Object.keys(row || {}).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );

    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              {columns.map((col) => (
                <th key={col} className="px-3 py-2 text-left font-semibold">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {metric.information.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col} className="px-3 py-2 text-gray-800">
                    {typeof row[col] === 'number' ? formatNumber(row[col]) : row[col] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">방문 통계</h1>
          <p className="text-sm text-gray-600 mt-1">
            {config.siteUrl || 'Microsoft Clarity'} • 최근 {config.numOfDays}일
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetched && (
            <div className="text-xs text-gray-500">
              {lastFetched.toLocaleString()}
            </div>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title="설정"
          >
            ⚙️
          </button>
        </div>
      </div>

      {/* 설정 패널 */}
      {showSettings && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">🔧 API 설정</h2>
            <div className="space-x-2">
              <button onClick={saveConfig} className="px-3 py-1.5 rounded-md text-xs bg-blue-600 text-white hover:bg-blue-700">
                저장
              </button>
              <button onClick={clearToken} className="px-3 py-1.5 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50">
                토큰 지우기
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">사이트 주소</label>
              <input
                type="text"
                value={config.siteUrl}
                onChange={(e) => setConfig((prev) => ({ ...prev, siteUrl: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                placeholder="https://example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Clarity 토큰</label>
              <input
                type="password"
                value={config.token}
                onChange={(e) => setConfig((prev) => ({ ...prev, token: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                placeholder="Bearer 토큰"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">조회 기간</label>
              <select
                value={config.numOfDays}
                onChange={(e) => setConfig((prev) => ({ ...prev, numOfDays: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="1">최근 1일</option>
                <option value="2">최근 2일</option>
                <option value="3">최근 3일</option>
              </select>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3">
            <div className="font-medium text-gray-700 mb-1">💡 토큰 발급</div>
            <div>clarity.microsoft.com → Settings → API Access → Generate Token</div>
            <div className="mt-1 text-amber-600">⚠️ 일 10회 호출 제한</div>
          </div>
        </div>
      )}

      {/* 모드 전환 */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
        <button
          onClick={() => setViewMode('simple')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            viewMode === 'simple' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          📊 간편 대시보드
        </button>
        <button
          onClick={() => setViewMode('advanced')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            viewMode === 'advanced' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          🔧 고급 모드
        </button>
        
        {viewMode === 'simple' && (
          <button
            onClick={loadDashboard}
            disabled={loading}
            className="ml-auto px-4 py-2 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? '불러오는 중...' : '🔄 통계 불러오기'}
          </button>
        )}
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* 간편 대시보드 */}
      {viewMode === 'simple' && (
        <div className="space-y-6">
          {!dashboardData && !loading && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="text-gray-400 text-5xl mb-4">📊</div>
              <p className="text-gray-600 mb-4">통계를 불러오려면 위의 버튼을 클릭하세요.</p>
              <p className="text-xs text-gray-500">한 번의 API 호출로 모든 핵심 지표를 확인할 수 있습니다.</p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">데이터를 불러오는 중...</span>
            </div>
          )}

          {dashboardData && !loading && (
            <>
              {/* 핵심 지표 카드 - 5개 */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-blue-600 text-xs font-medium mb-1">
                    <span>👥</span> 총 방문수
                  </div>
                  <div className="text-xl font-bold text-blue-900">
                    {trafficSummary ? formatNumber(trafficSummary.totalSessions) : '-'}
                  </div>
                  <div className="text-[10px] text-blue-600 mt-0.5">
                    실제: {trafficSummary ? formatNumber(trafficSummary.humanSessions) : '-'}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-green-600 text-xs font-medium mb-1">
                    <span>🧑</span> 고유 방문자
                  </div>
                  <div className="text-xl font-bold text-green-900">
                    {trafficSummary ? formatNumber(trafficSummary.distinctUsers) : '-'}
                  </div>
                  <div className="text-[10px] text-green-600 mt-0.5">
                    중복 제외
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-purple-600 text-xs font-medium mb-1">
                    <span>📄</span> 페이지/세션
                  </div>
                  <div className="text-xl font-bold text-purple-900">
                    {trafficSummary ? trafficSummary.pagesPerSession.toFixed(1) : '-'}
                  </div>
                  <div className="text-[10px] text-purple-600 mt-0.5">
                    평균 방문 페이지
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-amber-600 text-xs font-medium mb-1">
                    <span>⏱️</span> 체류시간
                  </div>
                  <div className="text-xl font-bold text-amber-900">
                    {engagementSummary ? formatDuration(engagementSummary.avgActiveTime) : '-'}
                  </div>
                  <div className="text-[10px] text-amber-600 mt-0.5">
                    활성 시간 평균
                  </div>
                </div>

                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-cyan-600 text-xs font-medium mb-1">
                    <span>📜</span> 스크롤 깊이
                  </div>
                  <div className="text-xl font-bold text-cyan-900">
                    {scrollSummary ? formatPercent(scrollSummary.avgScrollDepth) : '-'}
                  </div>
                  <div className="text-[10px] text-cyan-600 mt-0.5">
                    페이지 평균
                  </div>
                </div>
              </div>

              {/* 2열 레이아웃: 기기 분포 + 유입 경로 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-4">📱 기기별 방문</div>
                  {trafficSummary?.devices ? (
                    <SimpleDonutChart data={trafficSummary.devices} labelKey="name" valueKey="sessions" />
                  ) : (
                    <div className="text-sm text-gray-500">데이터 없음</div>
                  )}
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-4">🔗 유입 경로 TOP 5</div>
                  {trafficSummary?.sources ? (
                    <SimpleBarChart data={trafficSummary.sources} labelKey="name" valueKey="sessions" maxItems={5} color="blue" />
                  ) : (
                    <div className="text-sm text-gray-500">데이터 없음</div>
                  )}
                </div>
              </div>

              {/* 봇 트래픽 + UX 문제 + 해석 가이드 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-3">🤖 봇 트래픽</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {trafficSummary ? formatNumber(trafficSummary.totalBot) : '-'}
                      </div>
                      <div className="text-xs text-gray-500">봇 세션</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-rose-600">
                        {trafficSummary && trafficSummary.totalSessions > 0 
                          ? formatPercent((trafficSummary.totalBot / trafficSummary.totalSessions) * 100)
                          : '-'}
                      </div>
                      <div className="text-xs text-gray-500">전체 대비</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-3">⚠️ UX 문제 지표</div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">🖱️ 데드 클릭</span>
                      <span className="font-semibold text-rose-600">{uxIssues?.deadClicks?.count || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">😤 분노 클릭</span>
                      <span className="font-semibold text-rose-600">{uxIssues?.rageClicks?.count || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">↩️ 빠른 이탈</span>
                      <span className="font-semibold text-amber-600">{uxIssues?.quickbacks?.count || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">📜 과도한 스크롤</span>
                      <span className="font-semibold text-amber-600">{uxIssues?.excessiveScroll?.count || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">⚠️ 스크립트 오류</span>
                      <span className="font-semibold text-gray-600">{uxIssues?.scriptErrors?.count || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-blue-800 mb-2">💡 지표 해석 가이드</div>
                  <div className="text-xs text-blue-700 space-y-1">
                    <div>• <strong>페이지/세션 2↑</strong> = 양호</div>
                    <div>• <strong>체류시간 1분↑</strong> = 양호</div>
                    <div>• <strong>스크롤 50%↑</strong> = 양호</div>
                    <div>• <strong>데드/분노 클릭</strong> = UI 개선 필요</div>
                    <div>• <strong>빠른 이탈</strong> = 콘텐츠 개선 필요</div>
                  </div>
                </div>
              </div>

              {/* 유입 경로 해석 */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">🔗 유입 경로 해석</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600">
                  <div><strong>(직접 방문)</strong>: URL 직접 입력/북마크</div>
                  <div><strong>google/naver</strong>: 검색 엔진</div>
                  <div><strong>facebook/instagram</strong>: SNS</div>
                  <div><strong>도메인명</strong>: 외부 사이트 링크</div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* 고급 모드 */}
      {viewMode === 'advanced' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
            <div className="text-sm font-semibold text-gray-700">🔧 Dimension 설정</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['dimension1', 'dimension2', 'dimension3'].map((dim, idx) => (
                <div key={dim}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Dimension {idx + 1}</label>
                  <select
                    value={config[dim]}
                    onChange={(e) => setConfig((prev) => ({ ...prev, [dim]: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  >
                    {DIMENSION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || '(없음)'}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={loadAdvancedData}
                disabled={loading}
                className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
              >
                {loading ? '불러오는 중...' : '통계 불러오기'}
              </button>
            </div>
          </div>

          {advancedData.length > 0 && (
            <div className="space-y-4">
              {advancedData.map((metric, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
                  <div className="text-base font-semibold text-gray-900">{metric.metricName || 'Metric'}</div>
                  {renderTable(metric)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClarityAnalyticsPage;
